import { jsPDF } from 'jspdf';
import type { TaxReportData } from '../api/ReportsApi';

export function generateTaxReportPDF(data: TaxReportData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('Trader by Think Different Labs', 14, 15);

  // Generation date on the right
  doc.text('12-11-25', pageWidth - 14, 15, { align: 'right' });

  // Title (centered) - increased spacing from header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`Annual Tax Report - ${data.year}`, pageWidth / 2, 35, { align: 'center' });

  // Divider line below title
  doc.setDrawColor(0, 0, 0);
  doc.line(14, 40, pageWidth - 14, 40);

  // Customer Details Section
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Customer: Omri Haim', 14, 50);
  doc.text('Personal Id: 035721349', 14, 56);
  doc.text('Address: Zalman Shneour St 13, Tel Aviv, Israel', 14, 62);

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 68, pageWidth - 14, 68);

  // Broker Information
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Broker: ${data.broker.name}, Australia`, 14, 78);
  doc.text('Regulatory License Number: 001289978, 25509 BC 2019', 14, 84);

  // Algorithm Section
  doc.text('Algorithm:', 14, 94);
  doc.text('Provider: Mavrix.ai', 14, 100);

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 106, pageWidth - 14, 106);

  // Profit Summary Section
  let yPosition = 116;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Trading Performance Summary', 14, yPosition);

  yPosition += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  // Create a grid for metrics
  const metrics = [
    { label: 'Net Profit', value: formatCurrency(data.netProfit), bold: true },
    { label: 'Gross Profit', value: formatCurrency(data.grossProfit), bold: false },
    { label: 'Total Commission', value: formatCurrency(data.commission), bold: false },
    { label: 'Total Swap', value: formatCurrency(data.swap), bold: false },
  ];

  metrics.forEach(metric => {
    if (metric.bold) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
    }

    doc.text(metric.label, 14, yPosition);

    // Right-align the value (no color coding)
    doc.text(`$${metric.value}`, pageWidth - 14, yPosition, { align: 'right' });

    yPosition += metric.bold ? 12 : 8;
  });

  // Fund Transactions Section
  yPosition += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(14, yPosition, pageWidth - 14, yPosition);
  yPosition += 10;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Fund Transactions', 14, yPosition);

  yPosition += 10;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);

  // Table headers
  doc.text('Date', 14, yPosition);
  doc.text('Type', 50, yPosition);
  doc.text('Amount', pageWidth - 14, yPosition, { align: 'right' });

  yPosition += 2;
  doc.setDrawColor(220, 220, 220);
  doc.line(14, yPosition, pageWidth - 14, yPosition);
  yPosition += 6;

  // Transaction rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  data.fundTransactions.forEach(transaction => {
    // Check if we need a new page
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }

    const date = new Date(transaction.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    const typeLabel = transaction.type.charAt(0).toUpperCase() +
                     transaction.type.slice(1);

    doc.setTextColor(0, 0, 0);
    doc.text(date, 14, yPosition);
    doc.text(typeLabel, 50, yPosition);

    // No color coding - just show amount with sign
    const isDeposit = transaction.type === 'deposit';
    const amountStr = `${isDeposit ? '+' : '-'}$${formatCurrency(Math.abs(transaction.amount))}`;
    doc.text(amountStr, pageWidth - 14, yPosition, { align: 'right' });

    yPosition += 8;
  });

  // End of Year Balance
  yPosition += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(14, yPosition, pageWidth - 14, yPosition);
  yPosition += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('End of Year Balance', 14, yPosition);

  doc.text(`$${formatCurrency(data.endOfYearBalance)}`, pageWidth - 14, yPosition, { align: 'right' });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Save the PDF
  doc.save(`Tax_Report_${data.year}.pdf`);
}

function formatCurrency(value: number): string {
  return Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
